import React, { useState, useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import ROUTES, { getLobbyUrl } from 'constants/routes'
import primoImage from 'assets/products/primo.svg'
import duoImage from 'assets/products/duo.svg'
import ultimoImage from 'assets/products/ultimo.svg'
import nuovoImage from 'assets/products/nuovo.svg'
import wheelLoginImage from 'assets/illustrations/wheelLogin.webp'

// Redux
import { signInPlayer, exchangeAuth0Token, resumeActiveGame } from 'store/auth'
import { GAMES_ENTERED_FOR_ONBOARDING } from 'store/user/selectors'
import { USER_TYPES } from 'constants/users'
import { getEmailFromAuth0User } from 'utils/auth0'

const TEAM_CODE_SHORTCUT = { ctrlKey: true, shiftKey: true, key: 'a' }
const BRAND_NAME = {
  company: 'VOLVO',
  sim: 'SIM',
  division: 'CARS'
}

const VEHICLES = [
  { key: 'primo', image: primoImage, tone: 'light' },
  { key: 'duo', image: duoImage, tone: 'soft' },
  { key: 'ultimo', image: ultimoImage, tone: 'mid' },
  { key: 'nuovo', image: nuovoImage, tone: 'dark' }
]

export default function Login() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect, user } = useAuth0()
  const [code, setCode] = useState('')
  const [showTeamCodeForm, setShowTeamCodeForm] = useState(false)
  const exchangedAuth0Ref = useRef(false)
  const resumeAttemptedRef = useRef(false)
  const teamCodeInputRef = useRef(null)
  const { loggedIn, userType } = useSelector((store) => store.auth)
  const userInfo = useSelector((store) => store.user.info.data)

  useEffect(() => {
    if (!loggedIn) {
      resumeAttemptedRef.current = false
      return
    }

    // Players sign in via access_code (no Auth0) and do not see the post-login
    // onboarding; they go straight to the game.
    if (userType === USER_TYPES.player) {
      history.replace(ROUTES.APP_URL_HOME)
      return
    }

    // For Auth0 users, wait until /user info is loaded so we know whether
    // they still need the post-login tour (until they have entered 2 started games).
    if (!userInfo) return

    const shouldSeeOnboarding = (userInfo.games_entered_count ?? 0) < GAMES_ENTERED_FOR_ONBOARDING

    if (userType === USER_TYPES.facilitator) {
      if (shouldSeeOnboarding) {
        history.replace(ROUTES.APP_URL_GAME_SELECTION)
        return
      }
      const urlParams = new URLSearchParams(window.location.search)
      const joinCode = urlParams.get('join_code')
      if (joinCode) {
        history.replace(getLobbyUrl(joinCode))
      } else {
        history.replace(ROUTES.APP_URL_GAME_SELECTION)
      }
      return
    }

    if (userType === USER_TYPES.user) {
      const email = getEmailFromAuth0User(user)
      if (!email) {
        history.replace(ROUTES.APP_URL_GAME_SELECTION)
        return
      }
      if (shouldSeeOnboarding) {
        history.replace(ROUTES.APP_URL_GAME_SELECTION)
        return
      }
      if (resumeAttemptedRef.current) return
      resumeAttemptedRef.current = true
      dispatch(resumeActiveGame(email)).then(({ resumed }) => {
        if (!resumed) history.replace(ROUTES.APP_URL_GAME_SELECTION)
      })
    }
  }, [loggedIn, userType, history, user, dispatch, userInfo])

  useEffect(() => {
    if (!isAuthenticated || exchangedAuth0Ref.current) return
    getAccessTokenSilently()
      .then((token) => {
        exchangedAuth0Ref.current = true
        dispatch(exchangeAuth0Token(token))
      })
      .catch(() => {
        exchangedAuth0Ref.current = true
      })
  }, [isAuthenticated, getAccessTokenSilently, dispatch])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.ctrlKey === TEAM_CODE_SHORTCUT.ctrlKey &&
        e.shiftKey === TEAM_CODE_SHORTCUT.shiftKey &&
        e.key.toLowerCase() === TEAM_CODE_SHORTCUT.key
      ) {
        e.preventDefault()
        setShowTeamCodeForm((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (showTeamCodeForm) teamCodeInputRef.current?.focus()
  }, [showTeamCodeForm])

  const handleSignInWithEmail = () => {
    loginWithRedirect({ redirectUri: `${window.location.origin}/login` })
  }

  const handleTeamCodeSubmit = (e) => {
    e.preventDefault()
    if (code.trim()) {
      dispatch(signInPlayer({ access_code: code.trim() }))
    } else {
      toast.error(t('login.errors.no_code'), { autoClose: 3000 })
    }
  }

  return (
    <div className="login fade-in">
      <div className="login__corner-dot login__corner-dot--top-left" aria-hidden="true" />
      <div className="login__corner-dot login__corner-dot--top-right" aria-hidden="true" />
      <div className="login__corner-dot login__corner-dot--bottom-left" aria-hidden="true" />
      <div className="login__corner-dot login__corner-dot--bottom-right" aria-hidden="true" />

      <div className="login__content">
        <section className="login__hero">
          <div className="login__hero-copy">
            <div className="login__chip">
              <span className="login__chip-dot" />
              <span>{t('login.landing_chip')}</span>
            </div>

            <div className="login__brand">
              <div className="login__brand-mark" aria-hidden="true">
                <img src={wheelLoginImage} alt="" className="login__brand-mark-image" />
              </div>

              <div className="login__brand-copy">
                <div className="login__brand-heading">
                  <span className="login__brand-volvo">{BRAND_NAME.company}</span>
                  <span className="login__brand-sim">{BRAND_NAME.sim}</span>
                </div>
                <span className="login__brand-cars">{BRAND_NAME.division}</span>
              </div>
            </div>
          </div>

          <div className="login__actions">
            {showTeamCodeForm ? (
              <form className="login__form" onSubmit={handleTeamCodeSubmit}>
                <input
                  ref={teamCodeInputRef}
                  type="text"
                  className="login__input"
                  placeholder={t('login.team_code_placeholder')}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <button type="submit" className="action-button action-button--primary">
                  <span className="action-button__text">{t('login.enter_with_team_code')}</span>
                </button>
                <button
                  type="button"
                  className="action-button action-button--secondary login__back"
                  onClick={() => {
                    setShowTeamCodeForm(false)
                    setCode('')
                  }}
                >
                  <span className="action-button__text">{t('login.back_to_email')}</span>
                </button>
              </form>
            ) : (
              <div className="login__form">
                <button
                  type="button"
                  className="action-button action-button--primary"
                  onClick={handleSignInWithEmail}
                >
                  <span className="action-button__text">{t('login.sign_in_with_email')}</span>
                </button>
              </div>
            )}
            <p className="login__footer">
              {t('login.landing_footer', { year: new Date().getFullYear() })}
            </p>
          </div>
        </section>

        <aside className="login__vehicles" aria-hidden="true">
          <div className="login__vehicles-list">
            {VEHICLES.map(({ key, image, tone }) => (
              <div key={key} className={`login__vehicle-card login__vehicle-card--${tone}`}>
                <div className="login__vehicle-media">
                  <img src={image} alt="" className="login__vehicle-image" />
                </div>
                <div className="login__vehicle-meta">
                  <span className="login__vehicle-name">{key}</span>
                  <span className={`login__vehicle-indicator login__vehicle-indicator--${tone}`} />
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  )
}
